/**
 * Canvas colour.
 *
 * The DOM reads colour from CSS custom properties; the canvas can't. Rather
 * than duplicate the palette, we read the resolved values off `:root` once per
 * theme change and cache them as RGB triples. One source of truth, no drift.
 */

import type { Channel } from './types'

export type RGB = [number, number, number]

export interface Palette {
  theme: 'light' | 'dark'
  channel: Record<Channel, RGB>
  canvasCore: RGB
  canvasEdge: RGB
  grid: RGB
  gridAlpha: number
  ringAlpha: number
  /**
   * The neutral shade a bloom casts.
   *
   * On the night field a glow is light and keeps its own hue. On paper a wash
   * of the mark's own pigment sitting directly under the mark is the definition
   * of a smudge — what it should be is a cast shadow.
   */
  wash: RGB
  /** The lightest mark the medium can make. */
  hi: RGB
  /** The densest mark the medium can make — warm ink, not neutral black. */
  deepen: RGB
  text1: RGB
  accent: RGB
  /** Dark themes need additive glow; light themes need the strand to darken paper. */
  glowMode: 'lighter' | 'source-over'
  /** Multiplier on strand body. */
  strandScale: number
  baseAlpha: number
}

function parseRGB(input: string): RGB {
  const s = input.trim()
  const m = s.match(/rgba?\(([^)]+)\)/i)
  if (m) {
    const parts = m[1]!.split(/[\s,/]+/).filter(Boolean)
    return [Number(parts[0]) || 0, Number(parts[1]) || 0, Number(parts[2]) || 0]
  }
  const hex = s.replace('#', '')
  if (hex.length === 3) {
    return [
      parseInt(hex[0]! + hex[0]!, 16),
      parseInt(hex[1]! + hex[1]!, 16),
      parseInt(hex[2]! + hex[2]!, 16),
    ]
  }
  if (hex.length >= 6) {
    return [
      parseInt(hex.slice(0, 2), 16),
      parseInt(hex.slice(2, 4), 16),
      parseInt(hex.slice(4, 6), 16),
    ]
  }
  return [128, 128, 128]
}

export function readPalette(theme: 'light' | 'dark'): Palette {
  const cs = getComputedStyle(document.documentElement)
  const v = (name: string) => parseRGB(cs.getPropertyValue(name) || '#808080')
  const dark = theme === 'dark'
  return {
    theme,
    channel: {
      signal: v('--sem-signal'),
      weak: v('--sem-weak'),
      tension: v('--sem-tension'),
      gap: v('--sem-gap'),
      insight: v('--sem-insight'),
      refuted: v('--sem-refuted'),
      dead: v('--sem-dead'),
    },
    canvasCore: v('--canvas-core'),
    canvasEdge: v('--canvas-edge'),
    grid: v('--canvas-grid'),
    gridAlpha: Number(cs.getPropertyValue('--canvas-grid-alpha')) || (dark ? 0.09 : 0.11),
    ringAlpha: Number(cs.getPropertyValue('--canvas-ring-alpha')) || (dark ? 0.075 : 0.058),
    wash: v('--canvas-wash'),
    hi: v('--canvas-hi'),
    deepen: v('--canvas-deepen'),
    text1: v('--text-1'),
    accent: v('--accent'),
    glowMode: dark ? 'lighter' : 'source-over',
    // On paper a strand deposits pigment rather than emitting light, and ink on
    // a sheet carries less contrast than an additive line on black — so the
    // plate compensates with body.
    strandScale: dark ? 1 : 1.06,
    baseAlpha: 1,
  }
}

export const rgba = (c: RGB, a: number) => `rgba(${c[0]},${c[1]},${c[2]},${a})`

export function mix(a: RGB, b: RGB, t: number): RGB {
  return [
    Math.round(a[0] + (b[0] - a[0]) * t),
    Math.round(a[1] + (b[1] - a[1]) * t),
    Math.round(a[2] + (b[2] - a[2]) * t),
  ]
}

/** Nudge toward white — used for the hot core of an active strand. */
export function lift(c: RGB, t: number): RGB {
  return mix(c, [255, 255, 255], t)
}

/**
 * A mark's hot core, in whichever direction the medium allows.
 *
 * "Hot" means *this is happening now* — the growth cone's tip, the wave riding
 * the consolidating shaft, the travelling signal, the arrival flash. On the
 * night field that reads as emission, so the mark goes toward white. On paper
 * there is no such thing: an engraver going over a line twice makes it denser,
 * not whiter, and lifting toward white on a white sheet erases the mark
 * entirely — which is exactly what light mode was doing to every present-tense
 * event on the canvas.
 *
 * The 0.85 stops a t=1 highlight from going the whole way to solid ink.
 */
export function hot(c: RGB, t: number, p: Palette): RGB {
  return p.theme === 'dark' ? mix(c, p.hi, t) : mix(c, p.deepen, t * 0.85)
}

/**
 * The ring a node wears under the pointer, as an alpha on `text1` — both
 * renderers' one number (the selected ring is 0.7 in both themes).
 *
 * Held at 3:1, a mark's floor, against both ends of the field it is drawn on
 * (`styles/contrast.test.ts`). The night figure was the only figure once: 0.32
 * of near-white, 2.7:1 on the night field. Paper was then matched to that 2.7
 * rather than both being raised (D222) — 0.43, because an ink ring over a light
 * ground climbs contrast slower than a light ring over a dark one. Now 0.38 is
 * 3.25:1 at the night field's edge and 0.5 is 3.16:1 at paper's, each still a
 * clear step under the selected ring.
 */
export const HOVER_RING_ALPHA: Readonly<Record<Palette['theme'], number>> = {
  dark: 0.38,
  light: 0.5,
}
export const hoverRingAlpha = (p: Palette): number => HOVER_RING_ALPHA[p.theme]

/**
 * The colour a bloom is painted in.
 *
 * A halo of the mark's own hue, laid under the mark, is a smudge — and because
 * light mode composites `source-over` rather than clipping at white, five
 * overlapping strands accumulate toward full saturation. On paper the shade
 * goes neutral, so overlaps stack into a legible warm grey and the hue stays
 * where it is legible: in the mark itself.
 */
export function washed(c: RGB, p: Palette): RGB {
  return p.theme === 'dark' ? c : p.wash
}
